import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ButtonComponent } from '@libs/front/components/button/button.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { BugReportError, BugReportResult, BugReportService, bugReportError } from 'src/app/services/bug-report.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { BugReportLauncher } from './bug-report-launcher.service';
import { DialogFocus } from './dialog-focus';
import { enableWebglReadback } from './element-capture';
import { componentPath, cssSelector } from './element-description';
import { ElementPicker } from './element-picker.service';
import { ScreenshotCapture } from './screenshot-capture';

type Step = 'idle' | 'picking' | 'editing' | 'sent';

/** Bornes alignées sur celles du serveur. */
const MAX_TITLE_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 5000;

/**
 * Signalement de bug, présent sur toutes les pages, réservé aux sysadmins pour l'instant.
 *
 * Le déclencheur est une icône des navbars — un bouton flottant recouvrait le contenu de
 * certaines pages — qui passe par BugReportLauncher. La désignation de l'élément fautif, à la
 * souris ou au clavier, revient à ElementPicker, sa capture à ScreenshotCapture et le focus de la
 * popup à DialogFocus. Ce composant, monté à la racine, enchaîne les étapes : la popup demande un
 * titre et une description, l'envoi crée un ticket Jira côté serveur, qui seul détient le jeton.
 *
 * Encapsulation désactivée : le curseur de sélection s'applique à toute la page via une classe
 * sur body, et le cadre de survol d'ElementPicker est posé directement dans body. Toutes les
 * classes sont préfixées « bug-report ».
 */
@Component({
  selector: 'app-bug-report',
  templateUrl: './bug-report.component.html',
  styleUrls: ['./bug-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [FormsModule, TranslateModule, PopupComponent, ButtonComponent],
  providers: [ScreenshotCapture, DialogFocus],
})
export class BugReportComponent implements OnDestroy {
  protected readonly maxTitleLength = MAX_TITLE_LENGTH;
  protected readonly maxDescriptionLength = MAX_DESCRIPTION_LENGTH;
  protected readonly canReport = inject(KeycloakUtilisateurService).hasRole('sys_admin');

  protected readonly step = signal<Step>('idle');
  protected readonly isSending = signal(false);
  protected readonly targetComponents = signal('');
  protected readonly error = signal<BugReportError | null>(null);
  protected readonly result = signal<BugReportResult | null>(null);
  protected readonly screenshot = inject(ScreenshotCapture);
  protected title = '';
  protected description = '';

  private readonly bugReportService = inject(BugReportService);
  private readonly launcher = inject(BugReportLauncher);
  private readonly picker = inject(ElementPicker);
  private readonly dialogFocus = inject(DialogFocus);

  private readonly popup = viewChild(PopupComponent, { read: ElementRef });
  private readonly titleInput = viewChild<ElementRef<HTMLInputElement>>('titleInput');
  private readonly ticketLink = viewChild<ElementRef<HTMLAnchorElement>>('ticketLink');

  private target: Element | null = null;
  /** Envoi en cours, annulé si la popup se ferme avant la réponse. */
  private sending: Subscription | null = null;

  constructor() {
    if (!this.canReport) {
      return;
    }

    // Monté à la racine, le composant est construit avant toute vue 3D ou carte : leurs
    // contextes WebGL naîtront lisibles, et la capture ne les rendra plus noirs.
    enableWebglReadback();

    this.launcher.toggleRequests.pipe(takeUntilDestroyed()).subscribe(() => this.togglePicking());
    effect(() => this.launcher.picking.set(this.step() === 'picking'));
  }

  ngOnDestroy(): void {
    this.sending?.unsubscribe();
    this.picker.stop();
  }

  // ── Sélection ─────────────────────────────────────────────────────────────

  private togglePicking(): void {
    if (this.step() === 'picking') {
      this.picker.stop();
      this.close();
    } else if (this.step() === 'idle') {
      // Popup ouverte : le déclencheur n'a rien à démarrer, la popup a son propre bouton.
      this.startPicking();
    }
  }

  private startPicking(): void {
    this.target = null;
    this.step.set('picking');
    this.picker.start({
      picked: element => this.select(element),
      // Annuler — même une resélection — abandonne le signalement entier, comme la fermeture.
      cancelled: () => this.close(),
    });
  }

  /** Élément fautif désigné : la popup de saisie s'ouvre, prend le focus, et sa capture est prise. */
  private select(element: Element): void {
    this.target = element;
    this.targetComponents.set(componentPath(element));
    this.error.set(null);
    this.result.set(null);
    this.step.set('editing');
    this.dialogFocus.open(() => this.popup()?.nativeElement, () => this.titleInput()?.nativeElement);
    void this.screenshot.capture(element);
  }

  protected reselect(): void {
    this.screenshot.clear();
    this.dialogFocus.suspend();
    this.startPicking();
  }

  // ── Saisie et envoi ────────────────────────────────────────────────────────

  /** Titre et description sont tous deux requis pour créer le ticket. */
  protected canSend(): boolean {
    return !!this.title.trim() && !!this.description.trim() && !this.isSending() && !this.screenshot.isCapturing();
  }

  protected send(): void {
    if (!this.canSend()) {
      return;
    }

    this.isSending.set(true);
    this.error.set(null);

    this.sending?.unsubscribe();
    this.sending = this.bugReportService.send({
      title: this.title.trim(),
      description: this.description.trim(),
      pageUrl: window.location.href,
      elementSelector: this.target ? cssSelector(this.target) : '',
      componentPath: this.targetComponents(),
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth} × ${window.innerHeight}`,
      screenshot: this.screenshot.blob,
    }).subscribe({
      next: result => {
        this.sending = null;
        this.isSending.set(false);
        this.result.set(result);
        this.step.set('sent');
        // Le formulaire disparaît : le focus passe au lien du ticket au lieu de retomber sur la page.
        this.dialogFocus.moveTo(() => this.ticketLink()?.nativeElement);
      },
      error: (error: unknown) => {
        this.sending = null;
        this.isSending.set(false);
        this.error.set(bugReportError(error));
      },
    });
  }

  /**
   * Ferme la popup et oublie le signalement : une capture qui se termine après coup est ignorée,
   * pour qu'aucune ne remplisse un autre signalement. Le focus revient où il était avant
   * l'ouverture. Pas pendant l'envoi : le serveur crée le ticket même si le navigateur abandonne la
   * requête (voir BugReportsController), fermer laisserait croire au signalement annulé.
   */
  protected close(): void {
    if (this.isSending()) {
      return;
    }
    this.sending?.unsubscribe();
    this.sending = null;
    this.isSending.set(false);
    this.screenshot.clear();
    this.title = '';
    this.description = '';
    this.target = null;
    this.targetComponents.set('');
    this.error.set(null);
    this.result.set(null);
    this.step.set('idle');
    this.dialogFocus.close();
  }
}
